import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Zap,
  Plus,
  MoreVertical,
  Trash2,
  Eye,
  FolderOpen,
  Calendar,
  MapPin,
  LogOut,
  User,
  ChevronRight,
  Copy,
  GitCompare,
  Menu,
  MessageSquare,
  Users,
  Clock,
  TrendingUp,
  CheckCircle2,
  Wrench,
  BookOpen,
} from "lucide-react";

const ADMIN_EMAIL = "sujayeendra0711@gmail.com";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentLogins, setRecentLogins] = useState(null);
  const [loginsLoading, setLoginsLoading] = useState(false);

  const isAdmin = (user?.email || "").toLowerCase() === ADMIN_EMAIL;

  // Fetch projects once on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch recent logins whenever the logged-in user's email is resolved
  useEffect(() => {
    if (isAdmin) fetchRecentLogins();
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRecentLogins = async () => {
    setLoginsLoading(true);
    try {
      const res = await fetch(`${API}/admin/recent-logins?hours=24`, {
        credentials: "include",
      });
      if (res.ok) {
        setRecentLogins(await res.json());
      } else {
        toast.error(`Recent logins unavailable (${res.status})`);
      }
    } catch (e) {
      toast.error("Could not load recent logins");
    } finally {
      setLoginsLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/projects`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const duplicateProject = async (projectId) => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/duplicate`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        const newProject = await response.json();
        toast.success("Project duplicated");
        fetchProjects();
        navigate(`/project/${newProject.project_id}`);
      } else {
        toast.error("Failed to duplicate project");
      }
    } catch {
      toast.error("Failed to duplicate project");
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Project deleted");
        fetchProjects();
      } else {
        toast.error("Failed to delete project");
      }
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const STATUS_META = {
    draft:           { label: "Draft",           className: "bg-[#4a70681a] text-[#3d6b65]",       dot: "bg-[#4a7068]" },
    analyzing:       { label: "Analyzing",       className: "bg-[#68968915] text-[#689689]",       dot: "bg-[#689689]" },
    complete:        { label: "Analyzed",        className: "bg-emerald-500/10 text-emerald-400",   dot: "bg-emerald-500" },
    review:          { label: "Under Review",    className: "bg-amber-500/10 text-amber-400",       dot: "bg-amber-500" },
    approved:        { label: "Approved",        className: "bg-violet-500/10 text-violet-400",     dot: "bg-violet-500" },
    in_construction: { label: "In Construction", className: "bg-orange-500/10 text-orange-400",     dot: "bg-orange-500" },
  };

  const getStatusBadge = (status) => {
    const m = STATUS_META[status] || STATUS_META.draft;
    return (
      <Badge className={`${m.className} flex items-center gap-1.5`} variant="secondary">
        <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
        {m.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTimeAgo = (isoString) => {
    const diff = Math.floor((Date.now() - new Date(isoString)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f2ec" }} data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 z-40 hidden lg:block" style={{ backgroundColor: "#504136", color: "#8ab8b0" }}>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#689689" }}>
              <Zap className="w-6 h-6" style={{ color: "#faf9f6" }} />
            </div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif', color: "#faf9f6" }}>
              EV Hub Studio
            </span>
          </div>

          <nav className="space-y-2">
            <Link
              to="/dashboard"
              className="nav-link active"
              data-testid="nav-projects"
            >
              <FolderOpen className="w-5 h-5" />
              Projects
            </Link>
            <Link
              to="/compare"
              className="nav-link"
              data-testid="nav-compare"
            >
              <GitCompare className="w-5 h-5" />
              Compare
            </Link>
            <Link
              to="/charger-catalog"
              className="nav-link"
              data-testid="nav-charger-catalog"
            >
              <BookOpen className="w-5 h-5" />
              Charger Catalog
            </Link>
            <Link
              to="/contact"
              className="nav-link"
              data-testid="nav-contact"
            >
              <MessageSquare className="w-5 h-5" />
              Contact Us
            </Link>
            {(user?.email || "").toLowerCase() === "sujayeendra0711@gmail.com" && (
              <Link
                to="/inbox"
                className="nav-link"
                data-testid="nav-inbox"
              >
                <Eye className="w-5 h-5" />
                Inbox
              </Link>
            )}
          </nav>
        </div>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-6" style={{ borderTop: "1px solid #5e4e41" }}>
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#5e4e41" }}>
                <User className="w-5 h-5" style={{ color: "#8ab8b0" }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: "#faf9f6" }}>{user?.name}</p>
              <p className="text-xs truncate" style={{ color: "#4a7068" }}>{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start transition-colors"
            style={{ color: "#4a7068" }}
            onMouseEnter={e => { e.currentTarget.style.color = "#faf9f6"; e.currentTarget.style.backgroundColor = "#5e4e41"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "#4a7068"; e.currentTarget.style.backgroundColor = "transparent"; }}
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40" style={{ backgroundColor: "#504136", borderBottom: "1px solid #5e4e41" }}>
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#689689" }}>
              <Zap className="w-5 h-5" style={{ color: "#faf9f6" }} />
            </div>
            <span className="font-bold text-lg" style={{ color: "#faf9f6" }}>EV Hub Studio</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" style={{ color: "#8ab8b0" }}>
                <Menu className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs font-normal" style={{ color: "#4a7068" }}>
                {user?.name}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                  <FolderOpen className="w-4 h-4" />
                  Projects
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/compare" className="flex items-center gap-2 cursor-pointer">
                  <GitCompare className="w-4 h-4" />
                  Compare
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/charger-catalog" className="flex items-center gap-2 cursor-pointer">
                  <BookOpen className="w-4 h-4" />
                  Charger Catalog
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/contact" className="flex items-center gap-2 cursor-pointer">
                  <MessageSquare className="w-4 h-4" />
                  Contact Us
                </Link>
              </DropdownMenuItem>
              {(user?.email || "").toLowerCase() === "sujayeendra0711@gmail.com" && (
                <DropdownMenuItem asChild>
                  <Link to="/inbox" className="flex items-center gap-2 cursor-pointer">
                    <Eye className="w-4 h-4" />
                    Inbox
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1
                className="text-2xl lg:text-3xl font-bold"
                style={{ fontFamily: 'Chivo, sans-serif', color: "#504136" }}
              >
                Projects
              </h1>
              <p className="mt-1 font-medium" style={{ color: "#4a7068" }}>
                Manage your EV charging station designs
              </p>
            </div>
            <Button
              onClick={() => navigate("/new-project")}
              style={{ backgroundColor: "#689689", color: "#faf9f6" }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#83E8BA"; e.currentTarget.style.color = "#504136"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#689689"; e.currentTarget.style.color = "#faf9f6"; }}
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>

          {/* Analytics strip */}
          {!loading && projects.length > 0 && (() => {
            const totalKw = projects.reduce((sum, p) => {
              const cfg = p.site_inputs?.charger_config || [];
              const POWER = { "22kW_AC": 22, "30kW_DC": 30, "60kW_DC": 60, "120kW_DC": 120, "150kW_DC": 150 };
              return sum + cfg.reduce((s, c) => s + (POWER[c.type] || 60) * (c.quantity || 0), 0);
            }, 0);
            const byCounts = Object.fromEntries(
              ["draft","complete","review","approved","in_construction"].map(s => [
                s, projects.filter(p => (p.status || "draft") === s).length
              ])
            );
            const analyzed = (byCounts.complete || 0) + (byCounts.review || 0) +
              (byCounts.approved || 0) + (byCounts.in_construction || 0);
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="rounded-xl px-4 py-3" style={{ backgroundColor: "#ffffff", border: "1px solid #B2E6D4" }}>
                  <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: "#504136" }}>Total Projects</p>
                  <p className="text-2xl font-bold mt-0.5" style={{ color: "#504136" }}>{projects.length}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#4a7068" }}>{byCounts.draft || 0} in design</p>
                </div>
                <div className="rounded-xl px-4 py-3" style={{ backgroundColor: "#ffffff", border: "1px solid #B2E6D4" }}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Zap className="w-3.5 h-3.5" style={{ color: "#689689" }} />
                    <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: "#504136" }}>Planned Capacity</p>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: "#504136" }}>{(totalKw / 1000).toFixed(1)} MW</p>
                  <p className="text-xs mt-0.5" style={{ color: "#4a7068" }}>{totalKw.toLocaleString()} kW total</p>
                </div>
                <div className="rounded-xl px-4 py-3" style={{ backgroundColor: "#ffffff", border: "1px solid #B2E6D4" }}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: "#504136" }}>Analyzed</p>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: "#504136" }}>{analyzed}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#4a7068" }}>{byCounts.approved || 0} approved</p>
                </div>
                <div className="rounded-xl px-4 py-3" style={{ backgroundColor: "#ffffff", border: "1px solid #B2E6D4" }}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Wrench className="w-3.5 h-3.5 text-orange-500" />
                    <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: "#504136" }}>In Construction</p>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: "#504136" }}>{byCounts.in_construction || 0}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#4a7068" }}>of {projects.length} projects</p>
                </div>
              </div>
            );
          })()}

          {/* Projects Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} style={{ borderColor: "#B2E6D4" }}>
                  <CardContent className="p-6">
                    <div className="skeleton h-6 w-3/4 mb-4"></div>
                    <div className="skeleton h-4 w-full mb-2"></div>
                    <div className="skeleton h-4 w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : projects.length === 0 ? (
            <Card className="border-dashed" style={{ borderColor: "#B2E6D4" }}>
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#edf8f4" }}>
                  <FolderOpen className="w-8 h-8" style={{ color: "#4a7068" }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: "#504136" }}>
                  No projects yet
                </h3>
                <p className="mb-6" style={{ color: "#4a7068" }}>
                  Create your first EV charging station project
                </p>
                <Button
                  onClick={() => navigate("/new-project")}
                  style={{ backgroundColor: "#689689", color: "#faf9f6" }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#83E8BA"; e.currentTarget.style.color = "#504136"; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#689689"; e.currentTarget.style.color = "#faf9f6"; }}
                  data-testid="empty-new-project-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <motion.div
                  key={project.project_id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.07 }}
                >
                  <Card
                    className="card-hover cursor-pointer group"
                    style={{ backgroundColor: "#ffffff", border: "1px solid #B2E6D4" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#8ab0a8"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#B2E6D4"; }}
                    data-testid={`project-card-${project.project_id}`}
                    onClick={() => navigate(`/project/${project.project_id}`)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle
                          className="text-lg font-semibold transition-colors"
                          style={{ color: "#504136" }}
                          onMouseEnter={e => { e.currentTarget.style.color = "#689689"; }}
                          onMouseLeave={e => { e.currentTarget.style.color = "#504136"; }}
                        >
                          {project.name}
                        </CardTitle>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/project/${project.project_id}`);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateProject(project.project_id);
                              }}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteProject(project.project_id);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {project.description && (
                        <p className="text-sm mb-4 line-clamp-2" style={{ color: "#4a7068" }}>
                          {project.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-sm mb-4" style={{ color: "#4a7068" }}>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>
                            {project.site_inputs?.land_length}m × {project.site_inputs?.land_width}m
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        {getStatusBadge(project.status)}
                        <div className="flex items-center gap-1 text-xs" style={{ color: "#4a7068" }}>
                          <Calendar className="w-3 h-3" />
                          {formatDate(project.updated_at)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
          {/* Recent Logins — admin only */}
          {isAdmin && (
            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: "#4a7068" }} />
                  <h2 className="text-lg font-semibold" style={{ fontFamily: 'Chivo, sans-serif', color: "#504136" }}>
                    Recent Logins
                  </h2>
                  {recentLogins && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: "#68968915", color: "#689689" }}>
                      {recentLogins.unique_users} user{recentLogins.unique_users !== 1 ? "s" : ""} · last 24h
                    </span>
                  )}
                </div>
                <button
                  onClick={fetchRecentLogins}
                  className="text-xs flex items-center gap-1 transition-colors"
                  style={{ color: "#4a7068" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#504136"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#4a7068"; }}
                >
                  <Clock className="w-3 h-3" />
                  Refresh
                </button>
              </div>

              <Card style={{ backgroundColor: "#ffffff", border: "1px solid #B2E6D4" }}>
                <CardContent className="p-0">
                  {loginsLoading ? (
                    <div className="p-6 space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="skeleton w-9 h-9 rounded-full" />
                          <div className="flex-1">
                            <div className="skeleton h-3.5 w-32 mb-1.5" />
                            <div className="skeleton h-3 w-48" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : recentLogins?.logins?.length === 0 ? (
                    <div className="p-8 text-center text-sm" style={{ color: "#4a7068" }}>
                      No logins in the last 24 hours
                    </div>
                  ) : (
                    <ul style={{ borderTop: "none" }}>
                      {recentLogins?.logins?.map((login, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-3 px-5 py-3.5 transition-colors"
                          style={{ borderBottom: "1px solid #B2E6D4" }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#edf8f4"; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                          {login.picture ? (
                            <img
                              src={login.picture}
                              alt={login.name}
                              className="w-9 h-9 rounded-full flex-shrink-0"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#edf8f4" }}>
                              <User className="w-4 h-4" style={{ color: "#4a7068" }} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: "#504136" }}>{login.name}</p>
                            <p className="text-xs truncate" style={{ color: "#4a7068" }}>{login.email}</p>
                          </div>
                          <span className="text-xs flex-shrink-0" style={{ color: "#4a7068" }}>
                            {formatTimeAgo(login.last_login)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
